import React from "react";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
} from "../../hooks/useAuthenticatedQuery";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { LIST_LOCATIONS, DELETE_LOCATION } from "../../graphql/operations";
import { Location } from "../../types/location";
import { Helmet } from "react-helmet-async";

const LocationList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: groupsLoading } = useUserGroups();
  const { data, loading, error, refetch } = useAuthenticatedQuery(
    LIST_LOCATIONS,
    {
      skip: !isAdmin, // Skip query if user is not admin
    }
  );

  const [deleteLocation] = useAuthenticatedMutation(DELETE_LOCATION);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this location?")) {
      try {
        await deleteLocation({ variables: { id } });
        refetch();
      } catch (error) {
        console.error("Error deleting location:", error);
      }
    }
  };

  if (!groupsLoading && !isAdmin) {
    return (
      <Box>
        <Helmet>
          <title>Almond Tree Dashboard - Access Denied</title>
        </Helmet>
        <Alert severity="error" sx={{ mt: 2 }}>
          You do not have permission to view locations. Please contact an
          administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;

  // if (loading || groupsLoading) return <Typography>Loading...</Typography>;
  // if (error) {
  //   return <Typography>Error loading employees: {error.message}</Typography>;
  // }

  const locations = data?.listLocations?.items || [];
  // console.log(locations);

  return (
    <Box>
      <Helmet>
        <title>Almond Tree Dashboard - Location List</title>
      </Helmet>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Locations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/dashboard/locations/new")}
          sx={{ backgroundColor: "#919749" }}
        >
          Add Location
        </Button>
      </Box>

      <Paper>
        {locations.map((location: Location) => (
          <Box
            key={location.id}
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-child": { borderBottom: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
              }}
            >
              <Box>
                <Typography variant="h6">{location.name}</Typography>
                <Typography color="textSecondary">
                  {location.address}, {location.city}, {location.state}{" "}
                  {location.zipCode}
                </Typography>
                <Typography>{location.phone}</Typography>
                <Typography>{location.email}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`Manager: ${location.manager}`}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Capacity: ${location.capacity}`}
                    size="small"
                    color="secondary"
                  />
                </Box>
              </Box>
              <Box>
                <IconButton
                  onClick={() => navigate(`/dashboard/locations/edit/${location.id}`)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDelete(location.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default LocationList;
